import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { creditNotesApi, studentsApi, configApi } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CurrencySelector from '../components/CurrencySelector';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MultiSelect } from '../components/MultiSelect';
import { Plus, Search, ChevronLeft, ChevronRight, Loader2, Download, FileText, CheckCircle, XCircle, Pencil, Lock } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { useBulkSelect, BulkDeleteButton, BulkSelectionBar } from '../components/BulkDelete';
import { toast } from 'sonner';

const formatCurrency = (amount, currency = 'USD') => {
  if (!amount && amount !== 0) return '-';
  return `${currency} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Credit notes carry the fee head they credit (added 2026-09-22). Older notes
// the backfill could not place have none - "none" filters for those.
const NOT_SET = 'none';

const CreditNotesPage = () => {
  const { isAdmin, userBranches } = useAuth();
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [branchFilter, setBranchFilter] = useState([]);
  const [classFilter, setClassFilter] = useState([]);
  // ?status=pending (Dashboard quick-access) pre-selects the status filter.
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(() => {
    const s = searchParams.get('status');
    return s ? s.split(',') : [];
  });
  const [feeHeadFilter, setFeeHeadFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentResults, setStudentResults] = useState([]);
  const [searchingStudent, setSearchingStudent] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const sel = useBulkSelect();
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${sel.size} selected credit note(s)? Approved ones will be reversed off their linked invoice. This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await creditNotesApi.bulkDelete(Array.from(sel.selected));
      toast.success(res.data.message || 'Deleted');
      sel.clear();
      fetchCreditNotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setBulkDeleting(false);
    }
  };

  const [newCN, setNewCN] = useState({
    student_id: '', amount: '', currency: 'USD', reason: '', invoice_id: '', fee_head: ''
  });

  useEffect(() => {
    configApi.get().then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const feeHeadDisplay = config?.fee_head_display || {};
  const feeHeadOptions = Object.entries(feeHeadDisplay).map(([value, label]) => ({ value, label }));
  const feeHeadLabel = (h) => (h ? (feeHeadDisplay[h] || h) : null);

  const fetchCreditNotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (branchFilter.length) params.branch = branchFilter.join(',');
      if (classFilter.length) params.current_class = classFilter.join(',');
      if (statusFilter.length) params.status = statusFilter.join(',');
      if (feeHeadFilter.length) params.fee_head = feeHeadFilter.join(',');
      if (searchQuery) params.search = searchQuery;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await creditNotesApi.getAll(params);
      setCreditNotes(response.data.credit_notes);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (err) {
      toast.error('Failed to load credit notes');
    } finally {
      setLoading(false);
    }
  }, [page, branchFilter, classFilter, statusFilter, feeHeadFilter, searchQuery, startDate, endDate]);

  useEffect(() => { fetchCreditNotes(); }, [fetchCreditNotes]);

  const searchStudents = async (query) => {
    if (!query || query.length < 2) { setStudentResults([]); return; }
    setSearchingStudent(true);
    try {
      const response = await studentsApi.getAll({ search: query, limit: 10 });
      setStudentResults(response.data.students);
    } catch (err) { console.error(err); }
    finally { setSearchingStudent(false); }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setNewCN({ ...newCN, student_id: student.id });
    setStudentResults([]);
    setStudentSearch('');
  };

  const handleCreateCN = async (e) => {
    e.preventDefault();
    if (!newCN.student_id) { toast.error('Please select a student'); return; }
    if (!newCN.fee_head) { toast.error('Please select a fee head'); return; }
    setSubmitting(true);
    try {
      await creditNotesApi.create({
        student_id: newCN.student_id,
        amount: parseFloat(newCN.amount),
        currency: newCN.currency,
        reason: newCN.reason,
        invoice_id: newCN.invoice_id || null,
        fee_head: newCN.fee_head
      });
      toast.success('Credit note created');
      setShowAddDialog(false);
      setNewCN({ student_id: '', amount: '', currency: 'USD', reason: '', invoice_id: '', fee_head: '' });
      setSelectedStudent(null);
      fetchCreditNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create credit note');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async (cn) => {
    try {
      toast.loading('Generating PDF...', { id: 'cn-pdf' });
      const response = await api.get(`/credit-notes/${cn.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `credit_note_${cn.credit_note_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded', { id: 'cn-pdf' });
    } catch (err) {
      toast.error('Failed to download PDF', { id: 'cn-pdf' });
    }
  };

  const handleApprove = async (cn) => {
    try {
      await creditNotesApi.approve(cn.id);
      toast.success('Credit note approved and applied to student balance');
      fetchCreditNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve credit note');
    }
  };

  const handleReject = async (cn) => {
    try {
      await creditNotesApi.reject(cn.id);
      toast.success('Credit note rejected');
      fetchCreditNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject credit note');
    }
  };

  // Edit state
  const [editCN, setEditCN] = useState(null);
  const [editCNData, setEditCNData] = useState({});
  const [editCNSubmitting, setEditCNSubmitting] = useState(false);

  const handleEditCNSave = async () => {
    if (!editCN) return;
    setEditCNSubmitting(true);
    try {
      await api.put(`/credit-notes/${editCN.id}`, editCNData);
      toast.success('Credit note updated');
      setEditCN(null);
      fetchCreditNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setEditCNSubmitting(false);
    }
  };

  const openCNEdit = (cn) => {
    setEditCN(cn);
    setEditCNData({ amount: cn.amount, currency: cn.currency || 'USD', reason: cn.reason || '', fee_head: cn.fee_head || '' });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1F1D]">Credit Notes</h1>
          <p className="text-[#5C635F] mt-1">{total} credit notes issued</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={(open) => {
          setShowAddDialog(open);
          if (!open) { setSelectedStudent(null); }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#3E3F96] hover:bg-[#2D2E7A]" data-testid="add-credit-note-btn">
              <Plus className="w-4 h-4 mr-2" />Issue Credit Note
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Issue Credit Note</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateCN} className="space-y-4">
              <div className="space-y-2">
                <Label>Student</Label>
                {selectedStudent ? (
                  <div className="flex items-center justify-between p-3 bg-[#F5F5FA] rounded-lg">
                    <div>
                      <p className="font-medium">{selectedStudent.first_name} {selectedStudent.last_name}</p>
                      <p className="text-sm text-[#5C635F]">{selectedStudent.student_id} - {selectedStudent.branch}</p>
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => {
                      setSelectedStudent(null);
                      setNewCN({ ...newCN, student_id: '' });
                    }}>Change</Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5C635F]" />
                    <Input placeholder="Search student..." value={studentSearch}
                      onChange={(e) => { setStudentSearch(e.target.value); searchStudents(e.target.value); }}
                      className="pl-9" data-testid="cn-student-search" />
                    {searchingStudent && <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin" />}
                    {studentResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {studentResults.map((s) => (
                          <button key={s.id} type="button" className="w-full px-3 py-2 text-left hover:bg-[#F5F5FA] flex justify-between"
                            onClick={() => handleStudentSelect(s)}>
                            <span className="font-medium">{s.first_name} {s.last_name}</span>
                            <span className="text-sm text-[#5C635F]">{s.student_id}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Amount</Label>
                <div className="flex gap-3 items-center">
                  <Input type="number" value={newCN.amount}
                    onChange={(e) => setNewCN({ ...newCN, amount: e.target.value })}
                    required min="0" step="0.01" className="flex-1" data-testid="cn-amount" />
                  <CurrencySelector value={newCN.currency} onChange={(c) => setNewCN({ ...newCN, currency: c })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Fee Head *</Label>
                <Select value={newCN.fee_head} onValueChange={(v) => setNewCN({ ...newCN, fee_head: v })}>
                  <SelectTrigger data-testid="cn-fee-head"><SelectValue placeholder="Which fee head does this credit?" /></SelectTrigger>
                  <SelectContent>
                    {feeHeadOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reason *</Label>
                <Textarea value={newCN.reason}
                  onChange={(e) => setNewCN({ ...newCN, reason: e.target.value })}
                  required placeholder="Reason for credit note..." data-testid="cn-reason" />
              </div>
              <div className="space-y-2">
                <Label>Invoice Reference (Optional)</Label>
                <Input value={newCN.invoice_id}
                  onChange={(e) => setNewCN({ ...newCN, invoice_id: e.target.value })}
                  placeholder="Invoice ID if applicable" data-testid="cn-invoice-ref" />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={submitting} data-testid="submit-cn-btn">
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Issue Credit Note
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* First row - Search and Branch/Class */}
            <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
              <div className="relative flex-1 min-w-48">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5C635F]" />
                <Input
                  placeholder="Search by student name or ID..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="pl-9"
                  data-testid="cn-search"
                />
              </div>
              <MultiSelect widthClass="w-full sm:w-36" placeholder="All Branches" testId="branch-filter"
                options={(isAdmin ? config?.branches : userBranches) || []}
                selected={branchFilter} onChange={(v) => { setBranchFilter(v); setPage(1); }} />
              <MultiSelect widthClass="w-full sm:w-36" placeholder="All Classes" testId="class-filter"
                options={config?.classes || []}
                selected={classFilter} onChange={(v) => { setClassFilter(v); setPage(1); }} />
            </div>
            {/* Second row - Status, Dates */}
            <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
              <MultiSelect widthClass="w-full sm:w-40" placeholder="All Status" testId="status-filter"
                options={[{ value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'rejected', label: 'Rejected' }]}
                selected={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(1); }} />
              <MultiSelect widthClass="w-full sm:w-44" placeholder="All Fee Heads" testId="fee-head-filter"
                options={[...feeHeadOptions, { value: NOT_SET, label: 'Not set' }]}
                selected={feeHeadFilter} onChange={(v) => { setFeeHeadFilter(v); setPage(1); }} />
              <div className="flex items-center gap-2">
                <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} className="w-36" data-testid="cn-start-date" />
                <span className="text-[#5C635F] text-sm">to</span>
                <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} className="w-36" data-testid="cn-end-date" />
              </div>
              {(searchQuery || branchFilter.length || classFilter.length || statusFilter.length || feeHeadFilter.length || startDate || endDate) && (
                <Button variant="ghost" size="sm" className="text-[#5C635F]" onClick={() => { 
                  setSearchQuery(''); setBranchFilter([]); setClassFilter([]); setStatusFilter([]); setFeeHeadFilter([]);
                  setStartDate(''); setEndDate(''); setPage(1); 
                }}>Clear filters</Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Floats over the page — selecting a row must not shift the table. */}
      {isAdmin && (
        <BulkSelectionBar count={sel.size} onClear={sel.clear}>
          <BulkDeleteButton count={sel.size} deleting={bulkDeleting} onClick={handleBulkDelete} label="credit notes" />
        </BulkSelectionBar>
      )}

      {/* Table */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#3E3F96]" /></div>
          ) : creditNotes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#5C635F]">
              <FileText className="w-12 h-12 mb-4 opacity-50" /><p>No credit notes found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F5FA] hover:bg-[#F5F5FA]">
                    {isAdmin && (
                      <TableHead className="w-8">
                        <Checkbox
                          checked={creditNotes.length > 0 && creditNotes.every((c) => sel.isSelected(c.id))}
                          onCheckedChange={() => sel.toggleAll(creditNotes.map((c) => c.id))}
                          aria-label="Select all"
                        />
                      </TableHead>
                    )}
                    <TableHead className="text-xs uppercase font-semibold">CN #</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Date</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Student</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Fee Head</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Reason</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-right">Amount</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Status</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {creditNotes.map((cn) => (
                    <TableRow key={cn.id} data-testid={`cn-row-${cn.credit_note_number}`}>
                      {isAdmin && (
                        <TableCell>
                          <Checkbox checked={sel.isSelected(cn.id)} onCheckedChange={() => sel.toggle(cn.id)} aria-label="Select row" />
                        </TableCell>
                      )}
                      <TableCell className="font-medium text-[#3E3F96]">{cn.credit_note_number}</TableCell>
                      <TableCell className="text-[#5C635F]">{formatDate(cn.created_at)}</TableCell>
                      <TableCell className="font-medium">{cn.student_name}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {cn.fee_head ? feeHeadLabel(cn.fee_head) : <span className="text-[#5C635F] italic">Not set</span>}
                      </TableCell>
                      <TableCell className="text-sm max-w-xs truncate">{cn.reason}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums text-[#3E3F96]">
                        {formatCurrency(cn.amount, cn.currency)}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          cn.status === 'approved' ? 'bg-green-100 text-green-700' :
                          cn.status === 'rejected' ? 'bg-red-100 text-red-600' :
                          'bg-yellow-100 text-yellow-700'
                        }>{cn.status}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          {isAdmin && (
                            cn.tally_posted ? (
                              <Button variant="ghost" size="sm" disabled title="Posted to Tally - Locked"><Lock className="w-4 h-4 text-[#999]" /></Button>
                            ) : (
                              <Button variant="ghost" size="sm" onClick={() => openCNEdit(cn)} title="Edit" data-testid={`edit-cn-${cn.credit_note_number}`}><Pencil className="w-4 h-4 text-[#3E3F96]" /></Button>
                            )
                          )}
                          {/* Approve/reject is admin-only on the backend (maker/checker),
                              so non-admins are not shown buttons that would 403. */}
                          {isAdmin && cn.status === 'pending' && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleApprove(cn)} title="Approve"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50" data-testid={`approve-cn-${cn.credit_note_number}`}>
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleReject(cn)} title="Reject"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`reject-cn-${cn.credit_note_number}`}>
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {cn.status === 'approved' && (
                            <Button variant="ghost" size="sm" onClick={() => downloadPdf(cn)} data-testid={`download-cn-${cn.credit_note_number}`}>
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#5C635F]">Showing {((page - 1) * 25) + 1} - {Math.min(page * 25, total)} of {total}</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm text-[#5C635F]">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      {/* Edit Credit Note Dialog */}
      <Dialog open={!!editCN} onOpenChange={(open) => { if (!open) setEditCN(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Credit Note {editCN?.credit_note_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input type="number" value={editCNData.amount || ''} onChange={(e) => setEditCNData({ ...editCNData, amount: parseFloat(e.target.value) })} min="0" step="0.01" data-testid="edit-cn-amount" />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={editCNData.currency} onValueChange={(v) => setEditCNData({ ...editCNData, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="UGX">UGX</SelectItem>
                    <SelectItem value="RWF">RWF</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Fee Head</Label>
              <Select value={editCNData.fee_head || ''} onValueChange={(v) => setEditCNData({ ...editCNData, fee_head: v })}>
                <SelectTrigger data-testid="edit-cn-fee-head"><SelectValue placeholder="Not set" /></SelectTrigger>
                <SelectContent>
                  {feeHeadOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea value={editCNData.reason || ''} onChange={(e) => setEditCNData({ ...editCNData, reason: e.target.value })} rows={3} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditCN(null)}>Cancel</Button>
              <Button className="bg-[#3E3F96] hover:bg-[#2D2E7A]" onClick={handleEditCNSave} disabled={editCNSubmitting} data-testid="save-edit-cn">
                {editCNSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditNotesPage;
